/**
 * Prompt Templates API hooks
 *
 * Hooks for managing prompt templates using the centralized React-Admin data layer.
 * Uses snake_case types to match backend API response directly.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { raClient, type RAListParams, type RAListResponse } from '@/lib/raClient'
import { createQueryKeys } from '@/lib/queryKeys'

// ============================================================================
// Types
// ============================================================================

/**
 * Prompt template - uses snake_case matching backend
 */
export interface PromptTemplate {
  id: number
  name: string
  description?: string
  content: string
  customer_id?: number
  created_at: string
  updated_at?: string
}

/**
 * Input for creating a prompt template
 */
export interface PromptTemplateCreateInput {
  name: string
  description?: string
  content: string
  customer_id?: number
}

/**
 * Input for updating a prompt template
 */
export interface PromptTemplateUpdateInput extends PromptTemplateCreateInput {
  id: number
}

/**
 * Filters for prompt template list
 */
export interface PromptTemplateFilters {
  name?: string
  customer_id?: number | null
}

// ============================================================================
// Query Keys
// ============================================================================

export const promptTemplateKeys = createQueryKeys('prompt-templates')

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch a paginated list of prompt templates
 */
export function usePromptTemplates(
  params: RAListParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: promptTemplateKeys.list(params),
    queryFn: () => raClient.getList<PromptTemplate>('prompt-templates', params),
    enabled: options.enabled ?? true,
    staleTime: 30 * 1000, // 30 seconds
  })
}

/**
 * Hook to fetch a single prompt template by ID
 */
export function usePromptTemplate(
  id: number | string | undefined,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: promptTemplateKeys.detail(id ?? ''),
    queryFn: () => raClient.getOne<PromptTemplate>('prompt-templates', id!),
    enabled: (options.enabled ?? true) && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to create a new prompt template
 */
export function useCreatePromptTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: PromptTemplateCreateInput): Promise<PromptTemplate> => {
      return raClient.create<PromptTemplate>('prompt-templates', data)
    },
    onSuccess: () => {
      // Invalidate all list queries
      queryClient.invalidateQueries({ queryKey: promptTemplateKeys.lists() })
    },
  })
}

/**
 * Hook to update an existing prompt template
 */
export function useUpdatePromptTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: PromptTemplateUpdateInput): Promise<PromptTemplate> => {
      return raClient.update<PromptTemplate>('prompt-templates', data.id, data)
    },
    onSuccess: (_, variables) => {
      // Invalidate detail and list queries
      queryClient.invalidateQueries({ queryKey: promptTemplateKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: promptTemplateKeys.lists() })
    },
  })
}

/**
 * Hook to delete a prompt template
 */
export function useDeletePromptTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      return raClient.delete('prompt-templates', id)
    },
    onSuccess: () => {
      // Invalidate all queries for this resource
      queryClient.invalidateQueries({ queryKey: promptTemplateKeys.all })
    },
  })
}
